import React from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import BaseCard from '../../../components/base/BaseCard';
import MainMetric from '../../../components/metrics/MainMetric';
import EffectiveMetric from '../../../components/metrics/EffectiveMetric';
import DetailGroup from '../../../components/metrics/DetailGroup';
import PositionTypeIndicator from '../../../components/metrics/PositionTypeIndicator';
import FitText from '../../../components/common/FitText';
import MultiExitTable from '../../../components/metrics/MultiExitTable';
import BigNumber from 'bignumber.js';
import { formatPriceLevel } from '../../../utils/pricePrecision';

const FuturesMetricsCard = ({
  metrics = {},
  contractSpec = {},
  values = {},
  isLoading = false,
  onSetValues = () => {},
  viewMode = 'full',
}) => {
  const formatValue = (value, options = {}) => {
    if (value === '--') return '--';
    const { isMoney = false, isQuantity = false, isRatio = false } = options;
    if (!value) return '0';
    try {
      if (!(value instanceof BigNumber)) {
        return '0';
      }

      const formatOptions = {
        decimalSeparator: '.',
        groupSeparator: ',',
        groupSize: 3
      };

      if (isMoney) {
        const num = value.toNumber();
        const prefix = num < 0 ? '-$' : '$';
        return prefix + Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      if (isQuantity) {
        return value.toFormat(0, formatOptions);
      }
      if (isRatio) {
        return value.toFormat(2, formatOptions) + ':1';
      }
      return value.toFormat(2, formatOptions);
    } catch (error) {
      console.error('Error formatting value:', error);
      return '0';
    }
  };

  const tickSize = contractSpec.tickSize || 0.25;
  // Real tick size, e.g. ES=0.25 -> 2dp, 6E=0.00005 -> 5dp — the instrument's
  // actual tradeable precision, not an arbitrary fixed cap.
  const tickDecimals = new BigNumber(tickSize).decimalPlaces() ?? 2;
  const formatPoints = (value) => formatPriceLevel(value, tickDecimals);

  // Convert points to ticks (industry standard for futures)
  const pointsToTicks = (points) => {
    if (!points || !(points instanceof BigNumber)) return 0;
    return Math.round(points.toNumber() / tickSize);
  };

  // Margin status
  const marginUtil = metrics.marginUtilization instanceof BigNumber
    ? metrics.marginUtilization.toNumber()
    : 0;
  const isOverLeveraged = marginUtil > 50;
  const isHighMargin = marginUtil > 25 && !isOverLeveraged;
  const marginStatus = isOverLeveraged ? 'Over-leveraged' : isHighMargin ? 'High' : 'Safe';
  const marginColor = isOverLeveraged ? 'text-red-400' : isHighMargin ? 'text-amber-400' : 'text-emerald-400';

  // Position sizing discreteness
  const floorContracts = metrics.floorContracts || 0;
  const ceilContracts = metrics.ceilContracts || 0;
  const floorDeviation = metrics.floorDeviation || 0;
  const ceilDeviation = metrics.ceilDeviation || 0;
  const selectedVariant = metrics.selectedVariant || 'floor';
  const tolerance = values.riskOvershootTolerance || 0;

  // Check if aggressive option exceeds tolerance
  const aggressiveExceedsTolerance = ceilDeviation > tolerance;

  // Format deviation for display
  const formatDeviation = (dev) => {
    const sign = dev >= 0 ? '+' : '';
    return `${sign}${dev.toFixed(1)}%`;
  };

  // Calculate risk for each variant (for display)
  const floorRisk = metrics.floorRisk instanceof BigNumber ? metrics.floorRisk : new BigNumber(0);
  const ceilRisk = metrics.ceilRisk instanceof BigNumber ? metrics.ceilRisk : new BigNumber(0);
  const riskPerContract = metrics.riskPerContract instanceof BigNumber ? metrics.riskPerContract : new BigNumber(0);

  // Quick action handler for zero contracts scenario
  const handleSetMinRisk = () => {
    // Set risk to minimum needed for 1 contract (rounded up for safety)
    if (riskPerContract.gt(0)) {
      const minRisk = riskPerContract.integerValue(BigNumber.ROUND_UP);
      onSetValues({ risk: minRisk });
    }
  };

  // --- Reusable blocks (identical in Full and Compact; only the arrangement changes) ---
  const contractsBlock = (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <div className="text-primary-orange uppercase tracking-wider text-xs font-medium">
          Contracts
        </div>
        {metrics.positionType && (
          <PositionTypeIndicator type={metrics.positionType} />
        )}
      </div>

      {/* Zero Contracts - Inline Quick Actions (Variant A from prototype) */}
      {floorContracts === 0 && ceilContracts === 0 && (
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-4xl font-bold text-slate-500 mb-1">
              <FitText align="left">0 {values.contract || 'ES'}</FitText>
            </div>
            <div className="text-slate-500 text-sm">
              <FitText align="left">Notional: $0.00</FitText>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-amber-500/20 max-w-[240px]">
            <div className="text-amber-400 text-xs font-medium mb-2 flex items-center gap-1">
              <AlertTriangle size={16} />
              Insufficient Risk
            </div>
            <div className="text-slate-400 text-xs mb-3">
              Minimum for 1 contract: <span className="text-white font-medium">{formatValue(riskPerContract, { isMoney: true })}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSetMinRisk}
                className="flex-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs px-3 py-1.5 rounded-lg transition-colors"
              >
                Set Risk {formatValue(riskPerContract, { isMoney: true })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dual Position Cards - Side by Side (Variant A from prototype) */}
      {(floorContracts > 0 || ceilContracts > 0) && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-2">
            {/* Conservative (Floor) Card - Click sets Risk to floorRisk */}
            <div
              onClick={() => onSetValues({ risk: floorRisk })}
              className={`rounded-xl p-4 border cursor-pointer transition-all ${
                selectedVariant === 'floor'
                  ? aggressiveExceedsTolerance
                    ? 'bg-blue-500/10 border-2 border-blue-500/50'
                    : 'bg-emerald-500/10 border-2 border-emerald-500/50'
                  : 'bg-slate-800/50 border border-slate-700 hover:bg-slate-800'
              }`}
              style={selectedVariant === 'floor' ? { boxShadow: aggressiveExceedsTolerance ? '0 0 20px rgba(59, 130, 246, 0.2)' : '0 0 20px rgba(16, 185, 129, 0.2)' } : {}}
            >
              <div className={`text-xs mb-1 flex items-center gap-1 ${
                selectedVariant === 'floor'
                  ? aggressiveExceedsTolerance ? 'text-blue-400' : 'text-emerald-400'
                  : 'text-slate-400'
              }`}>
                Conservative
                {selectedVariant === 'floor' && (
                  <Check size={12} />
                )}
              </div>
              <div className="text-2xl font-bold text-white">
                {floorContracts} {values.contract || 'ES'}
              </div>
              <div className="text-slate-500 text-xs mt-1">
                Risk: {formatValue(floorRisk, { isMoney: true })}
              </div>
              <div className="text-blue-400 text-xs">
                {formatDeviation(floorDeviation)} from target
              </div>
            </div>

            {/* Aggressive (Ceil) Card - Click sets Risk to ceilRisk */}
            <div
              onClick={() => onSetValues({ risk: ceilRisk })}
              className={`rounded-xl p-4 border cursor-pointer transition-all ${
                aggressiveExceedsTolerance
                  ? 'bg-slate-800/50 border border-red-500/30 opacity-60 hover:opacity-80'
                  : selectedVariant === 'ceil'
                    ? 'bg-emerald-500/10 border-2 border-emerald-500/50'
                    : 'bg-slate-800/50 border border-slate-700 hover:bg-slate-800'
              }`}
              style={selectedVariant === 'ceil' && !aggressiveExceedsTolerance ? { boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)' } : {}}
            >
              <div className={`text-xs mb-1 flex items-center gap-1 ${
                aggressiveExceedsTolerance
                  ? 'text-red-400'
                  : selectedVariant === 'ceil'
                    ? 'text-emerald-400'
                    : 'text-slate-400'
              }`}>
                Aggressive
                {aggressiveExceedsTolerance ? (
                  <AlertTriangle size={12} />
                ) : selectedVariant === 'ceil' && (
                  <Check size={12} />
                )}
              </div>
              <div className={`text-2xl font-bold ${aggressiveExceedsTolerance ? 'text-slate-400' : 'text-white'}`}>
                {ceilContracts} {values.contract || 'ES'}
              </div>
              <div className="text-slate-500 text-xs mt-1">
                Risk: {formatValue(ceilRisk, { isMoney: true })}
              </div>
              <div className={`text-xs ${
                aggressiveExceedsTolerance ? 'text-red-400' : 'text-emerald-400'
              }`}>
                {aggressiveExceedsTolerance
                  ? `${formatDeviation(ceilDeviation)} exceeds ${tolerance}% limit`
                  : `${formatDeviation(ceilDeviation)} (within ${tolerance}% tolerance)`
                }
              </div>
            </div>
          </div>

          <div className="text-slate-500 text-sm">
            <FitText align="left">
              Notional: {formatValue(metrics.notionalValue, { isMoney: true })}
            </FitText>
          </div>
        </>
      )}
    </div>
  );

  // Margin MainMetric (futures' equivalent of crypto's Leverage hero metric)
  const marginRequiredValue = formatValue(metrics.marginRequired, { isMoney: true });
  const marginRequiredSub = `Utilization: ${marginUtil.toFixed(1)}%`;
  const marginRequiredMetric = (
    <MainMetric label="Margin Required" value={marginRequiredValue} subValue={marginRequiredSub} />
  );

  // Multiple take-profits (scale-out)
  const multiOn = !!(values.multiTpEnabled && metrics.multiExit);
  const me = metrics.multiExit || {};
  const tpCount = values.tpCount || (me.rows ? me.rows.length : '');
  const multiExitTable = multiOn ? <MultiExitTable data={metrics.multiExit} mode={values.tpMode} pricePrefix="$" decimals={tickDecimals} /> : null;

  const netProfitMetric = (
    <EffectiveMetric
      label="Net Profit"
      value={formatValue(multiOn ? me.totalNetProfit : metrics.netProfit, { isMoney: true })}
      subValue={multiOn ? `${tpCount} take-profits` : `Gross: ${formatValue(metrics.grossProfit, { isMoney: true })}`}
      isPositive={true}
    />
  );
  const netRiskMetric = (
    <EffectiveMetric
      label="Net Risk"
      value={formatValue(metrics.netRisk, { isMoney: true })}
      subValue={`Gross: ${formatValue(metrics.grossRisk, { isMoney: true })}`}
      isPositive={false}
    />
  );
  // A price level, not a money amount — use formatPoints (full precision) rather
  // than formatValue's isMoney branch, which hardcodes 2dp.
  const avgExitStr = multiOn && me.avgExitPrice && me.avgExitPrice.isFinite && me.avgExitPrice.isFinite()
    ? `$${formatPoints(me.avgExitPrice)}` : '--';
  const requiredRRMetric = (
    <EffectiveMetric
      label={multiOn ? "Avg Exit" : "Required R:R"}
      value={multiOn ? avgExitStr : `${formatValue(metrics.requiredGrossRR)}:1`}
      subValue={multiOn ? `${tpCount} exits` : `To get ${formatValue(values.desiredRR)}:1 net`}
      isPositive={true}
    />
  );
  const grossRRMetric = (
    <EffectiveMetric
      label={multiOn ? "Blended R:R" : "Gross R:R"}
      value={multiOn ? `${formatValue(me.blendedNetRR)}:1` : `${formatValue(metrics.grossRR)}:1`}
      subValue={multiOn ? `Net · ${tpCount} exits` : `Net R:R: ${formatValue(metrics.netRR)}:1`}
      isPositive={true}
    />
  );

  // Distances (in ticks - industry standard for futures) — compact RIGHT column
  // Multi-TP: targets already live in the exits table — keep Distances clean.
  const tpDistItems = multiOn
    ? []
    : [
        { label: "Nominal TP", value: `$${formatPoints(metrics.takeProfit)}` },
        { label: "Optimized TP", value: `$${formatPoints(metrics.optimizedTakeProfit)}`, className: "text-emerald-400" },
      ];
  const distancesGroup = (
    <DetailGroup
      title="Distances"
      items={[
        { label: "Stop", value: `${pointsToTicks(metrics.stopPoints)} ticks` },
        ...(multiOn ? [] : [{ label: "Target", value: `${pointsToTicks(metrics.targetPoints)} ticks` }]),
        { label: "Break-even", value: `${pointsToTicks(metrics.breakEvenPoints)} ticks` },
        ...tpDistItems,
      ]}
    />
  );

  // Per-Anna: the per-line breakdown lives in Settings (which shows how the
  // round-turn fee is computed); the card body just shows the round-turn total.
  const feeBreakdownGroup = (
    <DetailGroup
      title="Fee"
      items={[
        {
          label: "Total Round Turn",
          value: formatValue(metrics.totalFees, { isMoney: true }),
          className: "text-amber-400"
        },
      ]}
    />
  );

  const marginBlock = (
    <div className="pt-1.5">
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-400 uppercase tracking-wider font-medium">Margin</span>
        <div className="h-px flex-1 bg-white/5 mr-[17px]" />
      </div>
      <div className="mt-1.5 flex flex-col leading-none space-y-1">
        <div className="flex justify-between text-xs py-1">
          <span className="text-slate-500 tracking-wide">Required</span>
          <span className="text-slate-400 tracking-wide">
            {formatValue(metrics.marginRequired, { isMoney: true })}
          </span>
        </div>
        <div className="flex justify-between text-xs py-1">
          <span className="text-slate-500 tracking-wide">Utilization</span>
          <span className={`tracking-wide ${marginColor}`}>
            {marginUtil.toFixed(1)}%
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden mt-1">
          <div
            className={`h-full rounded-full transition-all duration-300 ${
              isOverLeveraged ? 'bg-red-500' : isHighMargin ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
            style={{ width: `${Math.min(marginUtil, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs pt-1">
          <span className="text-slate-500 tracking-wide">Status</span>
          <span className={`tracking-wide font-medium ${marginColor}`}>
            {marginStatus}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <BaseCard className={isLoading ? 'opacity-50 transition-opacity duration-200' : ''}>
      <div className="pt-6 px-4 pb-6 sm:pt-10 sm:px-8 sm:pb-10">
        {viewMode === 'compact' ? (
          /* ----- COMPACT: Contracts + Margin (left) · 3 metrics row + Distances (right) ----- */
          <div className="[&_.text-4xl]:text-3xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 lg:items-start">
              {/* LEFT: Contracts, then Margin Required below it (like Full) */}
              <div className="space-y-6 lg:space-y-8">
                {contractsBlock}
                {marginRequiredMetric}
              </div>
              {/* RIGHT: Gross R:R / Net Profit / Net Risk in a row, Distances below */}
              <div>
                <div className="grid grid-cols-3 gap-3 sm:gap-5">
                  {grossRRMetric}
                  {netProfitMetric}
                  {netRiskMetric}
                </div>
                <div className="mt-5">
                  {distancesGroup}
                </div>
              </div>
            </div>
            {multiExitTable && <div className="mt-5">{multiExitTable}</div>}
          </div>
        ) : (
          /* ----- FULL: today's layout, made responsive ----- */
          <>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left column */}
              <div className="lg:col-span-5 space-y-6">
                {contractsBlock}
                {/* Secondary metric: Margin */}
                {marginRequiredMetric}
              </div>

              {/* Right column */}
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                {netProfitMetric}
                {netRiskMetric}
                {requiredRRMetric}
                {grossRRMetric}
              </div>
            </div>

            {multiExitTable && <div className="mt-8">{multiExitTable}</div>}

            {/* Bottom section - 3 columns */}
            <div className="pt-6 pb-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {distancesGroup}
                {feeBreakdownGroup}
                {marginBlock}
              </div>
            </div>
          </>
        )}
      </div>
    </BaseCard>
  );
};

export default FuturesMetricsCard;
